import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X, Search, User, ShoppingBag } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useAuthStore } from '../store/authStore';
import { useCartStore, useCartTotals } from '../store/cartStore';
import { useProducts } from '../hooks/useProducts';
import { analyticsService } from '../services/analytics.service';
import { formatCurrency } from '../utils/formatCurrency';
import { cn } from '../utils/cn';

const navLinks = [
  { label: 'Shop', to: '/shop' },
  { label: 'Our Story', to: '/about' },
  { label: 'Why Orenyva', to: '/why-orenyva' },
  { label: 'Reviews', to: '/reviews' },
  { label: 'FAQ', to: '/faq' },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const user = useAuthStore((s) => s.user);
  const openCart = useCartStore((s) => s.openCart);
  const { itemCount } = useCartTotals();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const { data: searchResults, isFetching } = useProducts(
    { search: searchTerm.trim(), limit: 5 },
    { enabled: searchOpen && searchTerm.trim().length > 1 },
  );

  const handleSearchSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    analyticsService.track('search', { metadata: { term: searchTerm.trim() } }).catch(() => {});
    navigate(`/shop?search=${encodeURIComponent(searchTerm.trim())}`);
    setSearchOpen(false);
    setSearchTerm('');
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-50 border-b border-beige/60 transition-colors duration-300',
        scrolled ? 'bg-[#FAF7EF]/95 shadow-soft backdrop-blur' : 'bg-[#FAF7EF]',
      )}
    >
      <div className="container-page flex h-20 items-center justify-between">
        <button
          type="button"
          aria-label="Open menu"
          className="flex h-10 w-10 items-center justify-center text-forest-dark md:hidden"
          onClick={() => setMobileOpen(true)}
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Brand Logo matching screenshot */}
        <Link to="/" className="flex items-center gap-2.5">
          <svg className="h-7 w-7 text-[#0e2c1e]" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17 8C8 10 59 16.5 17 8zM12 2C6.48 2 2 6.48 2 12c0 3.6 1.9 6.76 4.75 8.53C6.46 19.34 6 17.72 6 16c0-4.42 3.58-8 8-8 1.72 0 3.34.46 4.53 1.25C20.3 7.9 17.14 6 13.5 6 9.36 6 6 9.36 6 13.5c0 .35.03.7.08 1.04C4.84 13.12 4 11.16 4 9c0-4.41 3.59-8 8-8s8 3.59 8 8c0 .34-.02.68-.06 1.01C20.58 9.36 21 10.63 21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9c0-.68.08-1.34.22-1.97" />
          </svg>
          <div className="flex flex-col">
            <span className="font-heading text-2xl font-normal leading-none tracking-tight text-[#0e2c1e] md:text-3xl">
              Orényva
            </span>
            <span className="mt-0.5 text-[9px] font-semibold tracking-widest text-[#0e2c1e]/70 uppercase">
              Natural Oral Care
            </span>
          </div>
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              className={({ isActive }) =>
                cn(
                  'text-xs font-medium uppercase tracking-wider text-charcoal/80 transition-colors hover:text-forest',
                  isActive && 'text-forest font-semibold',
                )
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-1 md:gap-3">
          <button
            type="button"
            aria-label="Search"
            onClick={() => setSearchOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center text-[#0e2c1e] transition-colors hover:opacity-75"
          >
            <Search className="h-5 w-5" strokeWidth={1.5} />
          </button>
          <Link
            to={user ? '/account' : '/login'}
            aria-label={user ? 'Account' : 'Sign in'}
            className="hidden h-10 w-10 items-center justify-center text-[#0e2c1e] transition-colors hover:opacity-75 md:flex"
          >
            <User className="h-5 w-5" strokeWidth={1.5} />
          </Link>
          <button
            type="button"
            aria-label={`Cart, ${itemCount} items`}
            onClick={openCart}
            className="relative flex h-10 w-10 items-center justify-center text-[#0e2c1e] transition-colors hover:opacity-75"
          >
            <ShoppingBag className="h-5 w-5" strokeWidth={1.5} />
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#0e2c1e] px-1 text-[10px] font-bold text-ivory">
              {itemCount}
            </span>
          </button>
        </div>
      </div>

      {/* Search flyout */}
      <AnimatePresence>
        {searchOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-beige bg-ivory"
          >
            <div className="container-page py-5">
              <form onSubmit={handleSearchSubmit} className="flex gap-2">
                <input
                  autoFocus
                  type="search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search products..."
                  className="w-full rounded border border-beige bg-white px-4 py-3 text-sm outline-none focus:border-forest"
                />
                <button type="submit" className="btn-primary !px-6">
                  Search
                </button>
                <button
                  type="button"
                  aria-label="Close search"
                  onClick={() => setSearchOpen(false)}
                  className="flex h-11 w-11 shrink-0 items-center justify-center text-charcoal/60 hover:text-charcoal"
                >
                  <X className="h-5 w-5" />
                </button>
              </form>
              {searchTerm.trim().length > 1 && (
                <div className="mt-4">
                  {isFetching ? (
                    <p className="text-sm text-charcoal/50">Searching&hellip;</p>
                  ) : searchResults?.items.length ? (
                    <ul className="divide-y divide-beige">
                      {searchResults.items.map((p) => (
                        <li key={p.id}>
                          <Link
                            to={`/product/${p.slug}`}
                            onClick={() => setSearchOpen(false)}
                            className="flex items-center gap-3 py-3"
                          >
                            <img
                              src={p.images?.[0]?.url ?? '/images/product-set-packaging.png'}
                              alt=""
                              className="h-12 w-12 rounded bg-cream object-cover"
                            />
                            <span className="flex-1 text-sm text-charcoal">{p.name}</span>
                            <span className="text-sm font-medium text-forest-dark">{formatCurrency(p.price)}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-charcoal/50">No products found for &ldquo;{searchTerm}&rdquo;.</p>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile nav drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <div className="fixed inset-0 z-[1000] md:hidden">
            <motion.div
              className="absolute inset-0 bg-charcoal/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              className="absolute left-0 top-0 h-full w-full max-w-xs bg-ivory shadow-card"
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
            >
              <div className="flex items-center justify-between border-b border-beige px-5 py-5">
                <span className="font-heading text-2xl text-forest-dark">Orényva</span>
                <button
                  type="button"
                  aria-label="Close menu"
                  onClick={() => setMobileOpen(false)}
                  className="flex h-9 w-9 items-center justify-center text-charcoal/70"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <nav className="flex flex-col px-5 py-4">
                {navLinks.map((link) => (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    onClick={() => setMobileOpen(false)}
                    className="border-b border-beige py-4 text-xs font-semibold uppercase tracking-wider text-charcoal"
                  >
                    {link.label}
                  </NavLink>
                ))}
                <Link
                  to={user ? '/account' : '/login'}
                  onClick={() => setMobileOpen(false)}
                  className="py-4 text-xs font-semibold uppercase tracking-wider text-charcoal"
                >
                  {user ? 'My Account' : 'Sign In'}
                </Link>
              </nav>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </header>
  );
}
